import { HttpClient } from "./http-client.service";
import { ApiConfig } from "../models/api-service/api-config.model";
import { CONTROLLER } from "../config/endpoint.config";
import { ENV_CONFIG } from "../config/env.config";
import { msalInstance } from "../config/auth.config";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

export interface BlobViewParams {
  document_id: number;
  flow_id: number;
}

export interface BlobFetchResult {
  blob: Blob;
  contentType: string;
}

/**
 * Service for handling Azure Blob Storage operations
 */
export class BlobStorageService extends HttpClient {
  /**
   * Fetch a blob file for viewing
   * @param params - Parameters including document_id and flow_id
   * @param signal - AbortController signal for cancellation
   * @returns Blob content
   */
  public async fetchBlobForView(
    params: BlobViewParams,
    signal?: AbortSignal
  ): Promise<Blob> {
    const queryParams = new URLSearchParams({
      document_id: params.document_id.toString(),
      flow_id: params.flow_id.toString()
    });

    const config: ApiConfig = new ApiConfig(
      `api/${CONTROLLER.GDX}/storage/blob-view?${queryParams.toString()}`,
      "GET",
      undefined,
      undefined
    );

    const response = await this.invokeApiRaw(config, signal);

    if (!response.ok) {
      throw new Error(
        `Failed to download file: ${response.status} ${response.statusText}`
      );
    }

    return response.blob();
  }

  /**
   * Fetch a blob from a full URL (typically Azure blob storage URL)
   * @param blobUrl - The full URL of the blob
   * @returns Blob content and content type
   */
  public async fetchBlobFromUrl(blobUrl: string): Promise<BlobFetchResult> {
    const response = await fetch(blobUrl);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch blob: ${response.status} ${response.statusText}`
      );
    }

    const blob = await response.blob();
    const contentType = blob.type;

    return {
      blob,
      contentType
    };
  }

  /**
   * Determine content type from file extension
   * @param filename - The filename with extension
   * @returns Content type string
   */
  public static getContentTypeFromFilename(filename: string): string {
    const extension = filename.split(".").pop()?.toLowerCase();

    const contentTypeMap: Record<string, string> = {
      pdf: "application/pdf",
      doc: "application/msword",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      xls: "application/vnd.ms-excel",
      xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ppt: "application/vnd.ms-powerpoint",
      pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      txt: "text/plain",
      csv: "text/csv",
      json: "application/json",
      xml: "application/xml",
      zip: "application/zip",
      png: "image/png",
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      gif: "image/gif",
      svg: "image/svg+xml",
      html: "text/html",
      css: "text/css",
      js: "application/javascript",
      mp4: "video/mp4",
      mp3: "audio/mpeg"
    };

    return contentTypeMap[extension || ""] || "application/octet-stream";
  }

  /**
   * Invoke API with raw response (for blob downloads)
   * @param config - API configuration
   * @param signal - Abort signal for cancellation
   * @returns Raw Response object
   */
  private async invokeApiRaw(
    config: ApiConfig,
    signal?: AbortSignal
  ): Promise<Response> {
    // Get authentication token using MSAL
    const request = {
      scopes: [process.env.REACT_APP_API_SCOPE || ""]
    };

    // let tokenResult;
    // try {
    //   tokenResult = await msalInstance.acquireTokenSilent(request);
    // } catch (error: any) {
    //   if (error instanceof InteractionRequiredAuthError) {
    //     tokenResult = await msalInstance.acquireTokenPopup(request);
    //   } else {
    //     throw error;
    //   }
    // }

    // Construct full URL using ENV_CONFIG.API_URL
    const baseUrl = ENV_CONFIG.GDX_API_URL || "http://localhost:8000/api";
    const fullUrl = `${baseUrl}/${config.url}`;

    const headers: Record<string, string> = {
      // Authorization: `Bearer ${tokenResult.accessToken}`
    };

    const fetchOptions: RequestInit = {
      method: config.method,
      headers,
      signal
    };

    if (config.data) {
      fetchOptions.body = JSON.stringify(config.data);
      headers["Content-Type"] = "application/json";
    }

    return fetch(fullUrl, fetchOptions);
  }
}

// Export singleton instance
export const blobStorageService = new BlobStorageService();
